import React from "react";
import { getFileType, FilePreview } from "components/util/helpers/FilePreview";
import l from "components/util/l";

// What FilePreview still renders inline — everything else is hidden behind its
// "Temp hide" block, so those files show as a card and nothing more.
const PREVIEWABLE_TYPES = ["image", "video", "audio"];

const ConversationAttachment = ({ attachment, index }) => {

  // A location is not a file: it has no name, no preview and nothing to download — the
  // card links out to a map instead.
  if (attachment.type == "location")
    return <LocationAttachment key={`attachment-${index}`} data={attachment.data} />;

  const file = attachment.data;
  const fileType = getFileType(file.url);
  // Uploaded files keep the name they were stored under, prefixed with an id.
  const fileName = file.name?.includes("_")
    ? file.name?.split("_").slice(1).join("_")
    : file.name;
  const extension = fileName?.split(".").slice(1).pop()?.toUpperCase();
  // A screenshot is an image plus what the user wrote about it, and the comment is
  // usually the point: the image says where they pointed, the comment says what about.
  const annotations = attachment.annotations;
  const hasPreview = PREVIEWABLE_TYPES.includes(fileType);

  return (
    <div key={`attachment-${index}`} className={`attachment my-2 ${hasPreview ? "has-preview" : ""}`}>

      {hasPreview &&
        <div className="attachment-preview">
          <FilePreview file={file} />
        </div>}

      <div className="attachment-card d-flex align-items-center"
        title={fileName}
        onClick={() => window.open(file.url, "_blank")}>

        <span className={`attachment-icon type-${fileType}`}>
          <i className={`fas ${getFileIcon(fileType)}`} />
        </span>

        <span className="attachment-meta">
          <span className="attachment-name">{fileName}</span>
          <span className="attachment-kind">{[extension, getTypeLabel(fileType)].filter(Boolean).join(" · ")}</span>
        </span>

        <i className="fas fa-arrow-up-right-from-square attachment-open"
          title={l("Abrir en una pestaña nueva%CH012")} />

      </div>

      {annotations &&
        <div className="attachment-annotations d-flex align-items-start">
          <i className="fas fa-comment-dots" />
          <span>{annotations}</span>
        </div>}

    </div>
  );

}

const LocationAttachment = ({ data }) => {

  const latitude = data?.latitude;
  const longitude = data?.longitude;
  const coordinates = [latitude, longitude]
    .filter(value => value != null)
    .map(value => Number(value).toFixed(5))
    .join(", ");

  return (
    <div className="attachment my-2">
      <div className="attachment-card d-flex align-items-center"
        title={coordinates}
        onClick={() => window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, "_blank")}>

        <span className="attachment-icon type-location">
          <i className="fas fa-location-dot" />
        </span>

        <span className="attachment-meta">
          <span className="attachment-name">{l("Ubicación%CH023")}</span>
          <span className="attachment-kind">{coordinates || l("Ver en el mapa%CH025")}</span>
        </span>

        <i className="fas fa-arrow-up-right-from-square attachment-open"
          title={l("Ver en el mapa%CH025")} />

      </div>
    </div>
  );

}

const getFileIcon = (type) => {
  switch (type) {
    case 'word': return 'fa-file-word';
    case 'excel': return 'fa-file-excel';
    case 'powerpoint': return 'fa-file-powerpoint';
    case 'pdf': return 'fa-file-pdf';
    case 'archive': return 'fa-file-zipper';
    case 'html': return 'fa-file-code';
    case 'text': return 'fa-file-lines';
    case 'video': return 'fa-file-video';
    case 'audio': return 'fa-file-audio';
    case 'image': return 'fa-file-image';
    default: return 'fa-paperclip';
  }
};

const getTypeLabel = (type) => {
  switch (type) {
    case 'word': return l("Documento%CH007");
    case 'excel': return l("Hoja de cálculo%CH013");
    case 'powerpoint': return l("Presentación%CH014");
    case 'pdf': return l("PDF%CH015");
    case 'archive': return l("Comprimido%CH016");
    case 'html': return l("Página web%CH022");
    case 'text': return l("Texto%CH017");
    case 'video': return l("Video%CH018");
    case 'audio': return l("Audio%CH019");
    case 'image': return l("Imagen%CH020");
    default: return l("Archivo%CH021");
  }
};

export default ConversationAttachment;
